import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldAlert, AlertTriangle, Activity, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";

const RISK_CONFIG = {
  LOW: { color: "#22C55E", bg: "rgba(34,197,94,0.06)", border: "rgba(34,197,94,0.12)", label: "LOW" },
  MODERATE: { color: "#F59E0B", bg: "rgba(245,158,11,0.06)", border: "rgba(245,158,11,0.12)", label: "MOD" },
  HIGH: { color: "#EF4444", bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.12)", label: "HIGH" },
};

function PulseMetric({ label, value, unit = "", color = "#E2E8F0" }) {
  return (
    <div className="flex items-center gap-2" data-testid={`risk-metric-${label.toLowerCase().replace(/ /g, "-")}`}>
      <span style={{
        fontFamily: "'Inter Tight', sans-serif", fontSize: "8px",
        letterSpacing: "0.14em", color: "#475569", fontWeight: "500",
      }}>{label}</span>
      <AnimatePresence mode="wait">
        <motion.span
          key={value}
          initial={{ opacity: 0.4, y: 1 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          style={{
            fontFamily: "'JetBrains Mono', monospace", fontSize: "11px",
            fontWeight: "600", color, fontVariantNumeric: "tabular-nums",
          }}
        >
          {value}{unit}
        </motion.span>
      </AnimatePresence>
    </div>
  );
}

export default function RiskPulseBar() {
  const [pulse, setPulse] = useState(null);
  const [activeAlert, setActiveAlert] = useState(0);
  const prevRisk = useRef(null);
  const [riskShift, setRiskShift] = useState(false);

  useEffect(() => {
    const fetchPulse = async () => {
      try {
        const { data } = await axios.get(`${API}/risk/pulse`);
        // Detect risk level change
        if (prevRisk.current && prevRisk.current !== data.overall_risk) {
          setRiskShift(true);
          setTimeout(() => setRiskShift(false), 3000);
        }
        prevRisk.current = data.overall_risk;
        setPulse(data);
      } catch {
        // silent — bar simply doesn't show if no data
      }
    };
    fetchPulse();
    const iv = setInterval(fetchPulse, 10000); // refresh every 10s for "live" feel
    return () => clearInterval(iv);
  }, []);

  // Cycle through alerts
  useEffect(() => {
    if (!pulse?.alerts?.length) return;
    const iv = setInterval(() => {
      setActiveAlert(i => (i + 1) % pulse.alerts.length);
    }, 5000);
    return () => clearInterval(iv);
  }, [pulse?.alerts?.length]);

  if (!pulse) return null;

  const risk = RISK_CONFIG[pulse.overall_risk] || RISK_CONFIG.LOW;
  const hasAlerts = pulse.alerts && pulse.alerts.length > 0;
  const currentAlert = hasAlerts ? pulse.alerts[activeAlert % pulse.alerts.length] : null;

  return (
    <div
      data-testid="risk-pulse-bar"
      style={{
        height: "32px",
        borderBottom: `1px solid ${risk.border}`,
        background: riskShift
          ? `linear-gradient(90deg, ${risk.bg}, rgba(11,13,16,0.98) 40%)`
          : "rgba(11,13,16,0.98)",
        backdropFilter: "blur(16px)",
        transition: "background 1.5s ease, border-color 1.5s ease",
        zIndex: 41,
        flexShrink: 0,
      }}
      className="flex items-center px-5 gap-4"
    >
      {/* Risk level badge */}
      <div className="flex items-center gap-2" data-testid="risk-level-badge">
        <div style={{
          width: "6px", height: "6px", borderRadius: "50%",
          background: risk.color,
          boxShadow: `0 0 8px ${risk.color}40`,
          animation: pulse.overall_risk !== "LOW" ? "live-dot 2s ease-in-out infinite" : "none",
        }} />
        <span style={{
          fontFamily: "'Inter Tight', sans-serif", fontSize: "9px",
          fontWeight: "700", letterSpacing: "0.14em", color: risk.color,
        }}>
          RISK: {risk.label}
        </span>
      </div>

      <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.04)" }} />

      {/* Metrics */}
      <PulseMetric label="EXPOSURE" value={pulse.total_exposure_pct?.toFixed(1)} unit="%" color={
        pulse.total_exposure_pct > 200 ? "#EF4444" : pulse.total_exposure_pct > 100 ? "#F59E0B" : "#E2E8F0"
      } />

      <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.04)" }} />

      <PulseMetric label="MARGIN" value={pulse.total_margin_pct?.toFixed(1)} unit="%" color={
        pulse.total_margin_pct > 80 ? "#EF4444" : pulse.total_margin_pct > 50 ? "#F59E0B" : "#38BDF8"
      } />

      <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.04)" }} />

      {pulse.liquidation_buffer_pct !== null && (
        <>
          <PulseMetric label="LIQ BUFFER" value={pulse.liquidation_buffer_pct?.toFixed(1)} unit="%" color={
            pulse.liquidation_buffer_pct < 5 ? "#EF4444" : pulse.liquidation_buffer_pct < 15 ? "#F59E0B" : "#22C55E"
          } />
          <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.04)" }} />
        </>
      )}

      <PulseMetric label="POSITIONS" value={pulse.total_positions} color="#94A3B8" />

      {/* Alert ticker */}
      {hasAlerts && (
        <>
          <div style={{ width: "1px", height: "14px", background: "rgba(255,255,255,0.04)" }} className="ml-auto" />
          <div className="flex items-center gap-2 ml-auto" data-testid="risk-alert-ticker">
            <AlertTriangle size={10} style={{
              color: currentAlert.level === "HIGH" ? "#EF4444" : "#F59E0B",
            }} />
            <AnimatePresence mode="wait">
              <motion.span
                key={activeAlert}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.3 }}
                style={{
                  fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "10px",
                  color: currentAlert.level === "HIGH" ? "#EF4444" : "#F59E0B",
                  maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {currentAlert.message}
              </motion.span>
            </AnimatePresence>
          </div>
        </>
      )}

      {/* Live timestamp */}
      <div className="flex items-center gap-1.5" style={{ marginLeft: hasAlerts ? "0" : "auto" }}>
        <Activity size={8} style={{ color: "#334155" }} />
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: "8px",
          color: "#334155", fontVariantNumeric: "tabular-nums",
        }}>
          {pulse.last_updated ? new Date(pulse.last_updated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "--"}
        </span>
      </div>
    </div>
  );
}
